import { Fragment, useEffect, useState } from "react"
import {
    AppBar,
    Box,
    Button,
    Container,
    Drawer,
    IconButton,
    Stack,
    Toolbar,
    Typography,
} from "@mui/material"
import { Close, ChevronRight, KeyboardArrowDown, Menu, OpenInNew } from "@mui/icons-material"
import { useLocation, useNavigate } from "react-router-dom"

import Logo from "../assets/Logo.png"
import { DRIVER_PORTAL_URL } from "../constants/links"
import { NAVBAR_HEIGHT } from "./NavbarOffset"
import { useEnquire } from "./EnquireModal"

const NAV_LINKS = [
    { path: "/", label: "Home" },
    { path: "/faqs", label: "FAQs" },
    { path: "/contact", label: "Contact" },
]

const resolveNavPath = (pathname) => {
    const path = pathname.toLowerCase()
    if (path === "/" || path === "/home") return "/"
    if (NAV_LINKS.some((link) => link.path === path)) return path
    if (path === "/vehicles") return "/vehicles"
    return false
}

const OttoNavLink = ({ label, selected, onClick, sx }) => (
    <Box
        component="button"
        type="button"
        onClick={onClick}
        aria-label={label}
        aria-current={selected ? "page" : undefined}
        sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.5,
            py: 0.75,
            border: 0,
            background: "transparent",
            cursor: "pointer",
            color: selected ? "#00A3FF" : "rgba(255, 255, 255, 0.92)",
            transition: "color 0.15s ease",
            whiteSpace: "nowrap",
            "&:hover": {
                color: "#00A3FF",
            },
            ...sx,
        }}
    >
        <Typography
            component="span"
            sx={{
                fontWeight: 800,
                fontSize: "0.95rem",
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
            }}
        >
            {label}
        </Typography>
        <KeyboardArrowDown sx={{ fontSize: 22, flexShrink: 0 }} />
    </Box>
)

const OttoMobileNavRow = ({ label, selected, onClick, href, external, icon = "down" }) => {
    const TrailingIcon = icon === "right" ? ChevronRight : KeyboardArrowDown

    return (
        <Box
            component={href ? "a" : "button"}
            type={href ? undefined : "button"}
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            onClick={onClick}
            aria-label={label}
            aria-current={selected ? "page" : undefined}
            sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: { xs: 2.5, sm: 3 },
                py: 2.25,
                border: 0,
                borderBottom: "1px solid rgba(0, 4, 53, 0.1)",
                background: "transparent",
                textDecoration: "none",
                cursor: "pointer",
                color: selected ? "#00A3FF" : "#000435",
                transition: "background-color 0.15s ease, color 0.15s ease",
                "&:hover": {
                    backgroundColor: "rgba(0, 4, 53, 0.03)",
                },
            }}
        >
            <Typography
                component="span"
                sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1.05rem", sm: "1.1rem" },
                    lineHeight: 1.25,
                    letterSpacing: "-0.01em",
                    textAlign: "left",
                }}
            >
                {label}
            </Typography>
            <TrailingIcon sx={{ fontSize: 24, flexShrink: 0, opacity: 0.9 }} />
        </Box>
    )
}

const Navbar = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const activePath = resolveNavPath(location.pathname)
    const openEnquire = useEnquire()

    const [expanded, setExpanded] = useState(false)

    useEffect(() => {
        window.scrollTo(0, 0)
        setExpanded(false)
    }, [location.pathname])

    const goTo = (path) => {
        setExpanded(false)
        navigate(path)
    }

    return (
        <Fragment>
            <AppBar sx={{ backgroundColor: "#000435" }}>
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{ gap: 1, minHeight: NAVBAR_HEIGHT }}>
                        <Box
                            component="img"
                            src={Logo}
                            sx={{ maxHeight: "42px", cursor: "pointer" }}
                            onClick={() => goTo("/")}
                            alt="Stargate Rentals"
                        />

                        <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{
                                display: { xs: "none", lg: "flex" },
                                ml: "auto",
                                mr: 1,
                                alignItems: "center",
                            }}
                        >
                            {NAV_LINKS.map((link) => (
                                <OttoNavLink
                                    key={link.path}
                                    {...link}
                                    selected={activePath === link.path}
                                    onClick={() => goTo(link.path)}
                                />
                            ))}
                        </Stack>

                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                display: { xs: "none", md: "flex" },
                                ml: { lg: 0, md: "auto" },
                                alignItems: "center",
                            }}
                        >
                            <Button
                                component="a"
                                href={DRIVER_PORTAL_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="text"
                                color="inherit"
                                endIcon={<OpenInNew sx={{ fontSize: 16, opacity: 0.75 }} />}
                                sx={{
                                    display: { xs: "none", md: "inline-flex" },
                                    textTransform: "none",
                                    fontWeight: 600,
                                    opacity: 0.9,
                                    px: 1.5,
                                    "&:hover": {
                                        opacity: 1,
                                        color: "secondary.main",
                                        backgroundColor: "transparent",
                                    },
                                }}
                            >
                                Driver portal
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                sx={{
                                    display: { xs: "none", md: "inline-flex" },
                                    borderRadius: 999,
                                    textTransform: "none",
                                    fontWeight: 700,
                                    px: 2.25,
                                }}
                                onClick={() => goTo("/vehicles")}
                            >
                                Browse vehicles
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                sx={{
                                    display: { xs: "none", md: "inline-flex" },
                                    borderRadius: 999,
                                    textTransform: "none",
                                    fontWeight: 700,
                                    px: 2.25,
                                }}
                                onClick={() => openEnquire?.()}
                            >
                                Enquire now
                            </Button>
                        </Stack>

                        <IconButton
                            onClick={() => setExpanded((prev) => !prev)}
                            sx={{
                                display: { xs: "flex", lg: "none" },
                                ml: "auto",
                                color: "inherit",
                            }}
                            aria-label="Open menu"
                        >
                            {expanded ? <Close /> : <Menu />}
                        </IconButton>
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer
                anchor="top"
                open={expanded}
                onClose={() => setExpanded(false)}
                PaperProps={{
                    sx: {
                        bgcolor: "#fff",
                        color: "#000435",
                    },
                }}
            >
                <Toolbar sx={{ bgcolor: "#000435", color: "#fff" }} />
                <Box sx={{ borderTop: "1px solid rgba(0, 4, 53, 0.1)" }}>
                    {NAV_LINKS.map((link) => (
                        <OttoMobileNavRow
                            key={link.path}
                            label={link.label}
                            selected={activePath === link.path}
                            onClick={() => goTo(link.path)}
                        />
                    ))}
                    <OttoMobileNavRow
                        label="Driver portal"
                        href={DRIVER_PORTAL_URL}
                        external
                        icon="right"
                        onClick={() => setExpanded(false)}
                    />
                </Box>
                <Stack sx={{ p: 2, gap: 1.25 }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700, py: 1.35 }}
                        onClick={() => goTo("/vehicles")}
                    >
                        Browse vehicles
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        sx={{
                            borderRadius: 999,
                            textTransform: "none",
                            fontWeight: 700,
                            py: 1.35,
                            borderColor: "rgba(0, 4, 53, 0.2)",
                            color: "#000435",
                        }}
                        onClick={() => {
                            setExpanded(false)
                            openEnquire?.()
                        }}
                    >
                        Enquire now
                    </Button>
                </Stack>
            </Drawer>
        </Fragment>
    )
}

export default Navbar
